import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import SkeletonGrid from "@/components/feature/SkeletonGrid";
import FilterBar, { type ContentType, type SortType } from "@/pages/category/components/FilterBar";
import { articles, categoryArticles, categoryTags, categoryVideos, getCategory, videos } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 4;

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = getCategory(categoryId);
  const [contentType, setContentType] = useState<ContentType>("article");
  const [sort, setSort] = useState<SortType>("latest");
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);

  const seoTitle = category ? `${category.name}资讯 - 抖阳` : "分类未找到 - 抖阳";
  const seoDescription = category
    ? `${category.name}频道：${category.description.slice(0, 70)}`
    : "该分类不存在，返回抖阳首页浏览最新原创内容。";

  useSeo({
    title: seoTitle,
    description: seoDescription,
    keywords: category ? `抖阳,${category.name},${category.name}资讯,原创内容` : "抖阳,分类",
  });

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 380);
    return () => window.clearTimeout(timer);
  }, [categoryId, contentType, sort, activeTags]);

  useEffect(() => {
    setContentType("article");
    setSort("latest");
    setActiveTags([]);
    setVisibleCount(PAGE_SIZE);
  }, [categoryId]);

  const tags = useMemo(() => (category ? categoryTags(category.id) : []), [category]);

  const list = useMemo(() => {
    if (!category) return [];
    const base = contentType === "article" ? categoryArticles(category.id) : categoryVideos(category.id);
    const filtered = activeTags.length
      ? base.filter((item) => item.tags.some((tag) => activeTags.includes(tag)))
      : base;
    return [...filtered].sort((a, b) =>
      sort === "latest" ? b.date.localeCompare(a.date) : b.views - a.views,
    );
  }, [category, contentType, activeTags, sort]);

  const visible = list.slice(0, visibleCount);
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(totalPages, Math.ceil(visibleCount / PAGE_SIZE));

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]));
    setVisibleCount(PAGE_SIZE);
  };

  const resetFilters = () => {
    setActiveTags([]);
    setSort("latest");
    setVisibleCount(PAGE_SIZE);
  };

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto w-full max-w-[900px] flex-1 px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-compass-3-line"
            title="分类不存在或已被调整"
            description="你访问的分类可能已经更名，回到首页看看最新的原创内容吧。"
            action={
              <Link
                to="/"
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600"
              >
                返回首页
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const otherArticles = articles.filter((item) => item.category !== category.id).slice(0, 3);
  const totalCount = categoryArticles(category.id).length + categoryVideos(category.id).length;

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100/70 px-4 pb-8 pt-5 md:px-6 md:pb-10 md:pt-6">
          <div className="mx-auto max-w-[1400px]">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />
            <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${category.icon} text-xl`} />
                  </span>
                  <div>
                    <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
                      {category.name}
                    </h1>
                    <p className="mt-1 font-label text-[11px] uppercase tracking-[0.24em] text-foreground-400">
                      {category.en} Channel
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {category.description}
                </p>
              </div>
              <dl className="flex shrink-0 gap-6 rounded-lg border border-background-200 bg-background-50 px-5 py-4">
                <div>
                  <dt className="text-xs text-foreground-500">文章</dt>
                  <dd className="mt-1 font-editorial text-xl font-bold text-foreground-950">
                    {categoryArticles(category.id).length}
                  </dd>
                </div>
                <div className="border-l border-background-200 pl-6">
                  <dt className="text-xs text-foreground-500">视频</dt>
                  <dd className="mt-1 font-editorial text-xl font-bold text-foreground-950">
                    {categoryVideos(category.id).length}
                  </dd>
                </div>
                <div className="border-l border-background-200 pl-6">
                  <dt className="text-xs text-foreground-500">全部</dt>
                  <dd className="mt-1 font-editorial text-xl font-bold text-primary-600">{totalCount}</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <FilterBar
            tags={tags}
            activeTags={activeTags}
            contentType={contentType}
            sort={sort}
            resultCount={list.length}
            onToggleType={(type) => {
              setContentType(type);
              setVisibleCount(PAGE_SIZE);
            }}
            onChangeSort={(value) => {
              setSort(value);
              setVisibleCount(PAGE_SIZE);
            }}
            onToggleTag={toggleTag}
            onReset={resetFilters}
          />

          <div className="mt-6">
            {loading ? (
              <SkeletonGrid count={4} columns="md:grid-cols-2" />
            ) : list.length === 0 ? (
              <EmptyState
                icon="ri-filter-off-line"
                title="未找到匹配内容"
                description="当前筛选条件下没有内容。可以尝试减少标签，或切换文章 / 视频类型。"
                action={
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600"
                  >
                    重置筛选
                  </button>
                }
              />
            ) : (
              <>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {visible.map((item) =>
                    contentType === "article" ? (
                      <ArticleCard key={item.id} article={item as (typeof articles)[number]} />
                    ) : (
                      <VideoCard key={item.id} video={item as (typeof videos)[number]} />
                    ),
                  )}
                </div>

                <div className="mt-8 flex flex-col items-center gap-4">
                  {visibleCount < list.length ? (
                    <button
                      type="button"
                      onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                      className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      加载更多内容
                      <i className="ri-arrow-down-line" />
                    </button>
                  ) : (
                    <p className="text-xs text-foreground-400">已经看到全部 {list.length} 条内容</p>
                  )}

                  {totalPages > 1 ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => setVisibleCount(Math.max(PAGE_SIZE, (currentPage - 1) * PAGE_SIZE))}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="上一页"
                      >
                        <i className="ri-arrow-left-s-line" />
                      </button>
                      {Array.from({ length: totalPages }).map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setVisibleCount((index + 1) * PAGE_SIZE)}
                          className={`h-8 w-8 cursor-pointer rounded-md text-xs transition-colors ${
                            currentPage === index + 1
                              ? "bg-foreground-950 text-background-50"
                              : "border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))}
                      <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => setVisibleCount(Math.min(list.length, currentPage * PAGE_SIZE + PAGE_SIZE))}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="下一页"
                      >
                        <i className="ri-arrow-right-s-line" />
                      </button>
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100/60 px-4 py-10 md:px-6 md:py-12">
          <div className="mx-auto max-w-[1400px]">
            <div className="mb-5 flex items-end justify-between gap-3">
              <h2 className="font-heading text-lg font-bold text-foreground-950">其他频道也在更新</h2>
              <Link to="/" className="flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600">
                回到首页
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {otherArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="horizontal" showSummary={false} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}